import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import {
  Box,
  Card,
  CardContent,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  SxProps,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import AdapterMoment from '@mui/lab/AdapterMoment';
import moment, { Moment } from 'moment';

import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../../components/BreadCrumb/Breadcrumbs';
import useTable, {
  defaultRowsPerPageOptions,
  TUseTableState,
} from '../../../../components/V2/Table/type1/useTable';
import TableHeadCustom, {
  THeadLabelItem,
} from '../../../../components/V2/Table/type1/TableHeadCustom';
import {
  adminGetMeetings,
  getScheduleState,
  resetAdminMeetingsData,
  TAdminGetMeetings,
} from '../../../../store/V2/ScheduleSlice';
import { useAppSelector } from '../../../../hooks';
import { ISection } from '../../../../store/V2/SectionSlice';
import TableNoData from '../../../../components/V2/Empty/TableNoData';
import TableLoader from '../../../../components/Loaders/TableLoader';
import UpcomingTableRow from './UpcomingTableRow';
import TablePaginationActions from '../../../../components/V2/Table/type1/TablePaginationActions';
import {
  defaultTextFieldProps,
  TPTextField,
} from '../../../../components/Inputs';
import { IUser } from '../../../../store/UserSlice';
import ViewUsersModal from './ViewUsersModal';
import CourseListDropdown, {
  TCourseSelectOption,
} from '../../../../components/Inputs/admin/CourseListDropdown';
import UserListDropdown, {
  TUserSelectOption,
} from '../../../../components/Inputs/admin/UserListDropdown';

export type TState = {
  startDate: Moment | null;
  endDate: Moment | null;
  selectedCourse: TCourseSelectOption | null;
  selectedTutor: TUserSelectOption | null;
  selectedStudent: TUserSelectOption | null;
};

export type TFilterData = {
  limit: number;
  offset: number;
  sortKey: string;
  sortValue: number;
  startDate?: string;
  endDate?: string;
  courseId?: string;
  tutorId?: string;
  studentId?: string;
};

const tableCardContainerStyles: SxProps = {
  borderRadius: '20px',
  boxShadow: '0px 14px 40px 0px #6E9DB22E',
};

const defaultListLimit = 30;
const defaultOrderBy = 'dateTime';
const defaultOrder = 'desc';
const defaultTableData: TUseTableState = {
  defaultOrderBy,
  defaultOrder,
  defaultDense: false,
  defaultCurrentPage: 0,
  defaultRowsPerPage: defaultRowsPerPageOptions[0],
  defaultSelected: [],
};

const defaultStartDate = moment();
const defaultEndDate = moment().add(1, 'week');

const defaultFilterData: TFilterData = {
  limit: defaultListLimit,
  offset: 0,
  sortKey: defaultOrderBy,
  sortValue: defaultOrder === 'desc' ? -1 : 1,
  startDate: defaultStartDate.clone().startOf('day').toISOString(),
  endDate: defaultEndDate.clone().endOf('day').toISOString(),
};

const defaultState: TState = {
  startDate: defaultStartDate.clone(),
  endDate: defaultEndDate.clone(),
  selectedCourse: null,
  selectedTutor: null,
  selectedStudent: null,
};

const tableHeaderLabels: THeadLabelItem[] = [
  {
    id: 'dateTime',
    label: 'Start',
    sortable: true,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'endTime',
    label: 'End',
    sortable: false,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'duration',
    label: 'Duration',
    sortable: true,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'tutor',
    label: 'Tutor',
    sortable: false,
    minWidth: 150,
    maxWidth: 150,
  },
  {
    id: 'students',
    label: 'Students',
    sortable: false,
    minWidth: 150,
    maxWidth: 150,
  },
  {
    id: 'lessonType',
    label: 'Lesson Type',
    sortable: true,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'paymentType',
    label: 'Payment Type',
    sortable: true,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'course',
    label: 'Course',
    sortable: false,
    minWidth: 180,
    maxWidth: 180,
  },
  {
    id: 'curriculum',
    label: 'Curriculum',
    sortable: false,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'subject',
    label: 'Subject',
    sortable: false,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'grade',
    label: 'Grade',
    sortable: false,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'tutorWaitingId',
    label: "Tutor's Link",
    sortable: false,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'studentWaitingId',
    label: "Student's Link",
    sortable: false,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'adminWaitingId',
    label: "Admin's Link",
    sortable: false,
    minWidth: 120,
    maxWidth: 120,
  },
];

const AdminUpcomingPage: FunctionComponent = () => {
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));
  const dispatch = useDispatch();

  // Store
  const scheduleState = useAppSelector(getScheduleState);

  // State
  const tableData = useTable(defaultTableData);
  const [state, setState] = useState({ ...defaultState });
  const [filterData, setFilterData] = useState({ ...defaultFilterData });
  const [viewStudentsModalData, setViewStudentsModalData] = useState<{
    open: boolean;
    users?: Array<IUser>;
  }>({ open: false });

  const { adminMeetingsData } = scheduleState;

  let meetingList: Array<ISection & { students: IUser[] }> = [];
  let totalCount = 0;

  if (adminMeetingsData) {
    totalCount = adminMeetingsData.totalDocs || 0;

    if (adminMeetingsData.list && adminMeetingsData.list.length) {
      meetingList = adminMeetingsData.list;
    }
  }

  useEffect(() => {
    getMeetingListData({ ...filterData });

    return () => {
      dispatch(resetAdminMeetingsData());
    };
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const getMeetingListData = (filterDataAttributes: TFilterData) => {
    const apiData: TAdminGetMeetings = {
      limit: filterDataAttributes.limit,
      offset: filterDataAttributes.offset,
    };

    if (filterDataAttributes.sortKey) {
      apiData.sortKey = filterDataAttributes.sortKey;
    }

    if (filterDataAttributes.sortValue) {
      apiData.sortValue = filterDataAttributes.sortValue;
    }

    if (filterDataAttributes.startDate) {
      apiData.startDate = filterDataAttributes.startDate;
    }

    if (filterDataAttributes.endDate) {
      apiData.endDate = filterDataAttributes.endDate;
    }

    if (filterDataAttributes.courseId) {
      apiData.courseId = filterDataAttributes.courseId;
    }

    if (filterDataAttributes.tutorId) {
      apiData.tutorId = filterDataAttributes.tutorId;
    }

    if (filterDataAttributes.studentId) {
      apiData.studentId = filterDataAttributes.studentId;
    }

    dispatch(adminGetMeetings({ ...apiData }));
    setFilterData({ ...filterDataAttributes });
  };

  const handleSort = (id: string) => {
    let newSortValue = 1;

    if (filterData.sortKey === id && filterData.sortValue === 1) {
      newSortValue = -1;
    }

    getMeetingListData({
      ...filterData,
      sortValue: newSortValue,
      sortKey: id,
    });

    tableData.onSort(id);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    getMeetingListData({
      ...filterData,
      offset: page * filterData.limit,
    });

    tableData.onChangePage(event, page);
    tableData.setSelected([]);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    getMeetingListData({
      ...filterData,
      offset: 0,
      limit: parseInt(event.target.value, 10),
    });

    tableData.onChangeRowsPerPage(event);
  };

  const refreshList = () => {
    getMeetingListData({ ...filterData });
  };

  const constructBreadcrumbOptions = () => {
    const options: Array<BreadcrumbOption> = [
      {
        type: 'link',
        title: 'Home',
        link: `${process.env.PUBLIC_URL}`,
      },
      {
        type: 'text',
        title: 'Upcoming',
        highlight: true,
      },
    ];

    return options;
  };

  const breadcrumbOptions = constructBreadcrumbOptions();

  return (
    <Container maxWidth="xl">
      <div style={{ padding: '30px 0' }}>
        <Breadcrumbs
          options={breadcrumbOptions}
          textColor="#647488"
          showDropDown={isXsScreen}
        />
      </div>
      <div>
        <Card sx={tableCardContainerStyles}>
          <CardContent sx={{ p: 0 }}>
            <div style={{ padding: '20px' }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Grid
                  container
                  alignItems="center"
                  columnSpacing="16px"
                  rowSpacing="20px"
                >
                  <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
                    <FormControl fullWidth>
                      <DatePicker
                        clearable
                        value={state.startDate}
                        label="Start Date"
                        inputFormat="YYYY-MMM-DD"
                        onChange={(date) => {
                          updateStateAttributes({
                            startDate: date,
                          });

                          getMeetingListData({
                            ...filterData,
                            startDate: date
                              ? date.clone().startOf('day').toISOString()
                              : undefined,
                          });
                        }}
                        renderInput={(params) => (
                          <TPTextField
                            {...params}
                            {...defaultTextFieldProps}
                            size="small"
                            placeholder="Start Date"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
                    <FormControl fullWidth>
                      <DatePicker
                        clearable
                        value={state.endDate}
                        label="End Date"
                        inputFormat="YYYY-MMM-DD"
                        onChange={(date) => {
                          updateStateAttributes({
                            endDate: date,
                          });

                          getMeetingListData({
                            ...filterData,
                            endDate: date
                              ? date.clone().endOf('day').toISOString()
                              : undefined,
                          });
                        }}
                        renderInput={(params) => (
                          <TPTextField
                            {...params}
                            {...defaultTextFieldProps}
                            size="small"
                            placeholder="End Date"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
                    <FormControl fullWidth>
                      <UserListDropdown
                        userTypes={['tutor']}
                        selectedItem={state.selectedTutor}
                        textFieldProps={{
                          label: 'Tutor',
                          placeholder: 'Filter by Tutor',
                          ...defaultTextFieldProps,
                        }}
                        handleChange={(e, value) => {
                          updateStateAttributes({
                            selectedTutor: value,
                          });

                          getMeetingListData({
                            ...filterData,
                            tutorId: value ? value.value : undefined,
                          });
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
                    <FormControl fullWidth>
                      <UserListDropdown
                        userTypes={['student']}
                        selectedItem={state.selectedStudent}
                        textFieldProps={{
                          label: 'Student',
                          placeholder: 'Filter by Student',
                          ...defaultTextFieldProps,
                        }}
                        handleChange={(e, value) => {
                          updateStateAttributes({
                            selectedStudent: value,
                          });

                          getMeetingListData({
                            ...filterData,
                            studentId: value ? value.value : undefined,
                          });
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
                    <FormControl fullWidth>
                      <CourseListDropdown
                        selectedItem={state.selectedCourse}
                        textFieldProps={{
                          label: 'Course',
                          placeholder: 'Filter by Course',
                          ...defaultTextFieldProps,
                        }}
                        handleChange={(e, value) => {
                          updateStateAttributes({
                            selectedCourse: value,
                          });

                          getMeetingListData({
                            ...filterData,
                            courseId: value ? value.value : undefined,
                          });
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
                    <Tooltip title="Refresh List">
                      <IconButton onClick={refreshList}>
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </LocalizationProvider>
            </div>
            <div>
              <TableContainer
                sx={{
                  position: 'relative',
                  maxHeight: 'calc(100vh - 250px)',
                  minHeight: '400px',
                }}
              >
                <Table stickyHeader size={tableData.dense ? 'small' : 'medium'}>
                  <TableHeadCustom
                    order={tableData.order}
                    orderBy={tableData.orderBy}
                    rowCount={meetingList.length}
                    headLabels={tableHeaderLabels}
                    numSelected={tableData.selected.length}
                    onSort={handleSort}
                  />
                  <TableBody>
                    {meetingList.map((row) => {
                      return (
                        <UpcomingTableRow
                          key={row._id}
                          section={row}
                          onStudentsClick={() =>
                            setViewStudentsModalData({
                              open: true,
                              users: row.students || [],
                            })
                          }
                        />
                      );
                    })}

                    {scheduleState.status !== 'loading' &&
                    !meetingList.length ? (
                      <TableNoData title="No meetings found" />
                    ) : null}

                    {scheduleState.status === 'loading' &&
                    !meetingList.length ? (
                      <TableLoader />
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ position: 'relative' }}>
                <TablePagination
                  rowsPerPageOptions={defaultRowsPerPageOptions}
                  component="div"
                  count={totalCount}
                  rowsPerPage={tableData.rowsPerPage}
                  page={tableData.page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  ActionsComponent={TablePaginationActions}
                />

                <FormControlLabel
                  label="Dense"
                  sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
                  control={
                    <Switch
                      checked={tableData.dense}
                      onChange={tableData.onChangeDense}
                    />
                  }
                />
              </Box>
            </div>
          </CardContent>
        </Card>
        {/* Modals */}
        {viewStudentsModalData.open ? (
          <ViewUsersModal
            open={viewStudentsModalData.open}
            onClose={() => setViewStudentsModalData({ open: false })}
            users={viewStudentsModalData.users || []}
            title="Students"
          />
        ) : null}
      </div>
    </Container>
  );
};

export default AdminUpcomingPage;
